export type UrlHash<T> = {
  toString: () => string;
  toHashString: () => string;
  toHashParamString: () => string;
  asParams: () => URLSearchParams;
  asObject: () => Record<string, string>;
  remove: <K extends keyof T>(
    keys: K[]
  ) => UrlHash<{ [P in Exclude<keyof T, K>]: T[P] }>;
  merge: (
    params: Record<string, any>,
    options?: {
      // If you pass a falsey value for these keys it will not be explicitly removed from the hash.
      retainKeys?: Array<string>;
    }
  ) => UrlHash<T>;
  clear: () => void;
  fromUrl: (url: string) => UrlHash<T>;
  toUrl: (url: string) => string;
};

export function getParamsFromHash(hash?: string | URLSearchParams | null) {
  if (!hash) {
    return new URLSearchParams();
  }

  if (hash instanceof URLSearchParams) {
    return hash;
  }

  if (hash.includes('?')) {
    const parts = hash.split('?');
    return new URLSearchParams(parts[1]);
  }

  return new URLSearchParams(hash.replace(/^#/, ''));
}

export function urlHash<T>(hash?: string | URLSearchParams): UrlHash<T> {
  let source = getParamsFromHash(hash);

  const asParams = () => {
    return source;
  };

  const asObject = () => {
    return Object.fromEntries(source.entries());
  };

  const remove = <K extends keyof T>(
    keys: K[]
  ): UrlHash<{ [P in Exclude<keyof T, K>]: T[P] }> => {
    for (const key of keys) {
      source.delete(key as string);
    }

    return urlHash(source.toString() || '');
  };

  const merge = <Y extends { [K in string]: string }>(
    params: Y,
    options?: {
      retainKeys?: Array<string>;
    }
  ): UrlHash<T & Y> => {
    for (const [key, value] of Object.entries(params)) {
      if (value) {
        source.set(key, value);
      } else {
        if (
          !options?.retainKeys?.find((retainedKey) => {
            return retainedKey === key;
          })
        ) {
          source.delete(key);
        }
      }
    }

    return urlHash(source.toString() || '');
  };

  /**
   * returns the key/value pairs joined with ampresand:
   *
   * @example a=true&b=1
   */
  const toString = () => {
    return source.toString();
  };

  /**
   * same as `toString` but prefixed with `?`
   *
   * @see {@link toString}
   */
  const toHashString = () => {
    return `?` + toString();
  };

  /**
   * Same as toHashString but prefixed with `#`
   */
  const toHashParamString = () => {
    return '#' + toHashString();
  };

  const fromUrl = (url: string) => {
    const target = new URL(url);
    return urlHash(target.hash);
  };

  const toUrl = (url: string) => {
    const target = new URL(url);
    target.hash = toString();
    return target.toString();
  };

  const clear = () => {
    source = new URLSearchParams();
  };

  return {
    merge,
    remove,
    asParams,
    asObject,
    toString,
    toHashString,
    toHashParamString,
    fromUrl,
    toUrl,
    clear,
  };
}
